import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

interface AuthUser {
  id: string
  name: string
  email: string | null
  picture: string | null
}

interface AuthState {
  user: AuthUser | null
  loading: boolean
  checked: boolean
  /**
   * True when the last auth check could not reach the identity provider (503).
   * The UI should show a "reconnecting" state rather than the Sign In button so
   * the user is not pushed back into the OAuth flow during a transient blip.
   */
  unavailable: boolean
  /** Signed in, but not a verified @replay.io account (LoopBuilder is internal). */
  accessDenied: boolean
}

interface AuthMeResponse {
  user: AuthUser | null
  accessDenied?: boolean
}

const initialState: AuthState = {
  user: null,
  loading: true,
  checked: false,
  unavailable: false,
  accessDenied: false,
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Guards against the concurrent duplicate call React StrictMode's double-invoked
// mount effect would otherwise make.
let fetchInFlight = false

/**
 * Fetch the current user from /api/auth-me.
 *
 * The endpoint distinguishes three outcomes and we must honor them:
 *  - 200 { user }      → signed in.
 *  - 200 { user:null } → genuinely signed out; clear the user.
 *  - 200 { user:null, accessDenied:true } → signed in, not a verified @replay.io account.
 *  - 503               → auth check unavailable (Auth0 throttled/unreachable).
 *                        Do NOT clear the user — retry with backoff, and if it
 *                        keeps failing reject as `unavailable` so the session is
 *                        left intact. This is the client half of the login-loop
 *                        fix (see docs/bugs/LoginLoop).
 */
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_: void, { rejectWithValue }) => {
    const maxAttempts = 4
    let delay = 500
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const last = attempt === maxAttempts - 1
      let response: Response
      try {
        response = await fetch('/api/auth-me')
      } catch {
        // Network error — validity unknown, same class as a 503.
        if (last) return rejectWithValue({ unavailable: true })
        await sleep(delay)
        delay *= 2
        continue
      }

      if (response.ok) {
        return (await response.json().catch(() => ({ user: null }))) as AuthMeResponse
      }

      // 503 (or any other non-OK) means we could not determine auth state.
      // Never treat this as signed out.
      if (last) return rejectWithValue({ unavailable: true })
      await sleep(delay)
      delay *= 2
    }
    return rejectWithValue({ unavailable: true })
  },
  {
    condition: () => {
      if (fetchInFlight) return false
      fetchInFlight = true
      return true
    },
  },
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        if (!state.checked) {
          state.loading = true
        }
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        fetchInFlight = false
        state.user = action.payload.user
        state.accessDenied = action.payload.accessDenied === true
        state.loading = false
        state.checked = true
        state.unavailable = false
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        fetchInFlight = false
        // Auth check failed — leave state.user untouched (never force a logout on
        // a transient failure) and surface the reconnecting state.
        state.loading = false
        state.checked = true
        state.unavailable = true
      })
  },
})

export default authSlice.reducer
